import React from 'react';
import { GraphType } from './graphType';
import { QuotationType } from 'features/shared/enums/quotationType';
import MeasuredValueDTO from 'features/shared/dtos/MeasuredValueDTO';

import styles from './measuredDataGraph.module.scss';
import { Bar, Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { convertCodeShortToLong } from 'features/shared/helpers/languageHelper';

type Props = {
  graphType: GraphType;
  quotationType: QuotationType;
  realValues: MeasuredValueDTO[];
  realValuesLabel: string;
  realValuesColor: string;
  planValues?: MeasuredValueDTO[];
  planValuesLabel?: string;
  showLegend?: boolean;
  containerClass?: string;
  onGraphClick?: () => void;
}

const MeasuredDataGraph = ({
  quotationType,
  realValues,
  realValuesLabel,
  realValuesColor,
  planValues,
  planValuesLabel,
  containerClass,
  graphType,
  showLegend = false,
  onGraphClick
}: Props) => {
  // translations
  const { i18n } = useTranslation();

  // helpers
  const getGraphOptions = () => {
    let graphOptions = {
      maintainAspectRatio: false,
      legend: {
        display: showLegend
      },
      scales: {
        yAxes: [
          {
            ticks: {
              beginAtZero: true,
            },
          },
        ],
      },
    };

    return graphOptions;
  }

  const getGraphPlugins = () => { 
    return [{
      id: 'click-anywhere',
      afterEvent(chart: any, args: any) {
        if (args.type === 'click') {
          if (onGraphClick) {
            onGraphClick();
          }
        }
      }
    }];
  }

  const getDataObj = () => {
    let data = {
      labels: getLabels(realValues),
      datasets: [
        {
          label: realValuesLabel,
          data: getData(realValues),
          backgroundColor: realValuesColor,
          borderColor: realValuesColor,
          borderWidth: graphType == "line" ? 2 : 0,
          fill: graphType == "line" ? false : true,
        }
      ],
    };

    if (planValues && planValuesLabel) {
      data.datasets.push({
        label: planValuesLabel,
        data: getData(planValues),
        backgroundColor: "#cfcfcf", // bar
        borderColor: "#cfcfcf", // line
        borderWidth: graphType == "line" ? 2 : 0,
        fill: graphType == "line" ? false : true,
      });
    }

    return data;
  };

  const getLabels = (values: MeasuredValueDTO[]) => {
    const formatOptions = getLabelDateFormat();
    const language = convertCodeShortToLong(i18n.language);

    let result = values.map((item) =>
      item.from ? new Date(item.from).toLocaleDateString(language, formatOptions) : "");

    return result;
  };

  const getLabelDateFormat = (): Intl.DateTimeFormatOptions => {
    switch (quotationType) {
      case "Year":
        return { year: "numeric"};
      case "Month":
        return { month: "long"};
      case "Day":
        return {};
      default:
        return {};
    }
  }

  const getData = (values: MeasuredValueDTO[]) => {
    let result = values.map((item) => (item.value ? item.value : 0));
    return result;
  };

  // sub-renders
  const renderGraph = () => {
    if (graphType == "bar") {
      return (
        <Bar data={getDataObj()} options={getGraphOptions()} plugins={getGraphPlugins()} />
      );
    } else if (graphType == "line") {
      return (
        <Line data={getDataObj()} options={getGraphOptions()} />
      );
    }
  };

  // render
  return (
    <div className={`${styles.container}${containerClass ? " " + containerClass : ""}`}>
      {renderGraph()}
    </div>
  );
}

export default MeasuredDataGraph;